import * as THREE from 'three';
import { createNameLabel } from '../utils/NameLabel.js';

export class Boss {
    constructor(scene, position, type, audioManager, world = null) {
        this.scene = scene;
        this.type = type; // 'clarence', 'mikenolan', 'lez'
        this.audioManager = audioManager;
        this.world = world; // For terrain collision
        
        // Base stats
        let baseHealth = 500;
        let baseDamage = 15;
        
        // Boss-specific multipliers
        if (type === 'mikenolan') {
            baseHealth *= 20; // 10,000 HP
            baseDamage *= 4;  // 60 damage
        } else if (type === 'lez') {
            baseHealth *= 100; // 50,000 HP
            baseDamage *= 8;   // 120 damage
        }
        
        this.health = baseHealth;
        this.maxHealth = baseHealth;
        this.damage = baseDamage;
        this.projectiles = [];
        this.lastAttackTime = 0;
        this.attackCooldown = 2;
        this.isDead = false;
        this.target = null;
        this.lastVoiceTime = 0;
        this.voiceCooldown = 8; // Speak every 8 seconds
        this.speed = (type === 'lez') ? 2 : 1.5; // Lez starts slower, speeds up after volcano
        this.canFly = (type === 'lez');
        this.isFlying = false; // Explicitly not flying on spawn
        this.lastVolcanoTime = 0;
        this.volcanoCooldown = 60; // Fly to volcano every 60 seconds
        
        this.createMesh(position);
        this.createHealthBar();
        
        // Speak on spawn
        setTimeout(() => {
            this.speak();
            // Mike Nolan speaks on spawn (no text box, just audio)
            if (this.type === 'mikenolan') {
                // Play Mike voice again for emphasis
                setTimeout(() => this.speak(), 1000);
            }
        }, 2000);
    }
    
    speak() {
        if (!this.audioManager) return;
        
        const currentTime = Date.now() / 1000;
        if (currentTime - this.lastVoiceTime < this.voiceCooldown) return;
        
        this.lastVoiceTime = currentTime;
        
        // Map boss types to voice character names
        const voiceMap = {
            'clarence': 'clarence',
            'mikenolan': 'mike',
            'lez': 'lez'
        };
        
        const voiceCharacter = voiceMap[this.type];
        if (voiceCharacter) {
            this.audioManager.playVoice(voiceCharacter);
        }
    }
    
    showTaunt(message) {
        // Create temporary taunt notification
        const taunt = document.createElement('div');
        taunt.style.cssText = `
            position: fixed;
            top: 30%;
            left: 50%;
            transform: translateX(-50%);
            background: rgba(0, 0, 0, 0.8);
            color: #ffff00;
            padding: 20px 40px;
            font-size: 24px;
            font-weight: bold;
            border: 3px solid #ffff00;
            border-radius: 10px;
            z-index: 1500;
            text-align: center;
            font-family: 'Courier New', monospace;
            box-shadow: 0 0 20px rgba(255, 255, 0, 0.5);
        `;
        taunt.textContent = message;
        document.body.appendChild(taunt);
        
        // Remove after 5 seconds
        setTimeout(() => {
            document.body.removeChild(taunt);
        }, 5000);
    }
    
    createMesh(position) {
        const group = new THREE.Group();
        
        if (this.type === 'clarence') {
            // Clarence - green potato head
            const headGeometry = new THREE.SphereGeometry(1.5, 16, 16);
            headGeometry.scale(1, 0.7, 1); // Flatten to potato shape
            const headMaterial = new THREE.MeshStandardMaterial({
                color: 0x6b8e23,
                roughness: 0.8
            });
            const head = new THREE.Mesh(headGeometry, headMaterial);
            head.position.y = 2;
            group.add(head);
            
            // Eyes
            const eyeGeometry = new THREE.SphereGeometry(0.15, 8, 8);
            const eyeMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
            const pupilMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
            
            const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
            leftEye.position.set(-0.4, 2.3, 1.2);
            group.add(leftEye);
            
            const leftPupil = new THREE.Mesh(new THREE.SphereGeometry(0.08, 8, 8), pupilMaterial);
            leftPupil.position.set(-0.4, 2.3, 1.3);
            group.add(leftPupil);
            
            const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
            rightEye.position.set(0.4, 2.3, 1.2);
            group.add(rightEye);
            
            const rightPupil = new THREE.Mesh(new THREE.SphereGeometry(0.08, 8, 8), pupilMaterial);
            rightPupil.position.set(0.4, 2.3, 1.3);
            group.add(rightPupil);
            
            // Body (tan vest)
            const bodyGeometry = new THREE.BoxGeometry(1.2, 1.5, 0.8);
            const bodyMaterial = new THREE.MeshStandardMaterial({ color: 0xd2b48c });
            const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
            body.position.y = 0.5;
            group.add(body);
            
            this.name = "CLARENCE";
            this.color = 0x6b8e23;
            
        } else if (this.type === 'mikenolan') {
            // Mike Nolan - high vis worker with sunglasses
            const headGeometry = new THREE.SphereGeometry(0.6, 16, 16);
            const headMaterial = new THREE.MeshStandardMaterial({ color: 0xd2b48c });
            const head = new THREE.Mesh(headGeometry, headMaterial);
            head.position.y = 2.5;
            group.add(head);
            
            // Sunglasses
            const glassesGeometry = new THREE.BoxGeometry(0.8, 0.2, 0.1);
            const glassesMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
            const glasses = new THREE.Mesh(glassesGeometry, glassesMaterial);
            glasses.position.set(0, 2.5, 0.6);
            group.add(glasses);
            
            // Beard
            const beardGeometry = new THREE.SphereGeometry(0.4, 8, 8);
            const beardMaterial = new THREE.MeshStandardMaterial({ color: 0x333333 });
            const beard = new THREE.Mesh(beardGeometry, beardMaterial);
            beard.position.set(0, 2.1, 0.5);
            group.add(beard);
            
            // High vis vest (yellow and purple)
            const vestGeometry = new THREE.BoxGeometry(1.5, 2, 1);
            const vestMaterial = new THREE.MeshStandardMaterial({ color: 0xffff00 });
            const vest = new THREE.Mesh(vestGeometry, vestMaterial);
            vest.position.y = 1;
            group.add(vest);
            
            // Purple collar
            const collarGeometry = new THREE.BoxGeometry(1.6, 0.3, 1.1);
            const collarMaterial = new THREE.MeshStandardMaterial({ color: 0x9370db });
            const collar = new THREE.Mesh(collarGeometry, collarMaterial);
            collar.position.y = 1.8;
            group.add(collar);
            
            this.name = "MIKE NOLAN";
            this.color = 0xffff00;
            
        } else if (this.type === 'lez') {
            // BIG LEZ - Human with PINK SINGLET!
            
            // Head (skin tone)
            const headGeometry = new THREE.SphereGeometry(0.7, 16, 16);
            const skinMaterial = new THREE.MeshStandardMaterial({ 
                color: 0xffdbac, // Skin tone
                roughness: 0.8
            });
            const head = new THREE.Mesh(headGeometry, skinMaterial);
            head.position.y = 2.5;
            group.add(head);
            
            // White hair (mullet)
            const hairGeometry = new THREE.SphereGeometry(0.75, 16, 16);
            hairGeometry.scale(1, 0.6, 1.3); // Flatten top, extend back
            const hairMaterial = new THREE.MeshStandardMaterial({ 
                color: 0xffffff, // WHITE hair
                roughness: 0.95
            });
            const hair = new THREE.Mesh(hairGeometry, hairMaterial);
            hair.position.set(0, 2.8, -0.1);
            group.add(hair);
            
            // Eyes
            const eyeGeometry = new THREE.CircleGeometry(0.12, 16);
            const eyeMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
            
            const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
            leftEye.position.set(-0.2, 2.6, 0.68);
            group.add(leftEye);
            
            const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
            rightEye.position.set(0.2, 2.6, 0.68);
            group.add(rightEye);
            
            // Pupils
            const pupilGeometry = new THREE.CircleGeometry(0.05, 16);
            const pupilMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
            
            const leftPupil = new THREE.Mesh(pupilGeometry, pupilMaterial);
            leftPupil.position.set(-0.2, 2.6, 0.69);
            group.add(leftPupil);
            
            const rightPupil = new THREE.Mesh(pupilGeometry, pupilMaterial);
            rightPupil.position.set(0.2, 2.6, 0.69);
            group.add(rightPupil);
            
            // Nose
            const noseGeometry = new THREE.ConeGeometry(0.08, 0.15, 8);
            noseGeometry.rotateX(Math.PI / 2);
            const nose = new THREE.Mesh(noseGeometry, skinMaterial);
            nose.position.set(0, 2.45, 0.72);
            group.add(nose);
            
            // PINK SINGLET (most important!)
            const singletGeometry = new THREE.CylinderGeometry(0.65, 0.75, 1.8, 12);
            const singletMaterial = new THREE.MeshStandardMaterial({ 
                color: 0xff69b4, // HOT PINK!
                roughness: 0.7,
                metalness: 0.1
            });
            const singlet = new THREE.Mesh(singletGeometry, singletMaterial);
            singlet.position.y = 1;
            singlet.name = 'singlet'; // Name it for easy finding
            group.add(singlet);
            
            // SHOULDERS (broad, muscular)
            const shoulderGeometry = new THREE.SphereGeometry(0.35, 12, 12);
            shoulderGeometry.scale(1.2, 0.8, 0.8); // Wider
            
            const leftShoulder = new THREE.Mesh(shoulderGeometry, skinMaterial);
            leftShoulder.position.set(-0.85, 1.7, 0);
            group.add(leftShoulder);
            
            const rightShoulder = new THREE.Mesh(shoulderGeometry, skinMaterial);
            rightShoulder.position.set(0.85, 1.7, 0);
            group.add(rightShoulder);
            
            // THICK Arms (skin tone) - MUCH thicker!
            const armGeometry = new THREE.CylinderGeometry(0.25, 0.22, 1.6, 8); // Was 0.15/0.13
            
            const leftArm = new THREE.Mesh(armGeometry, skinMaterial);
            leftArm.position.set(-0.95, 1.0, 0);
            leftArm.rotation.z = 0.3;
            group.add(leftArm);
            
            const rightArm = new THREE.Mesh(armGeometry, skinMaterial);
            rightArm.position.set(0.95, 1.0, 0);
            rightArm.rotation.z = -0.3;
            group.add(rightArm);
            
            // Light blue shorts
            const legGeometry = new THREE.CylinderGeometry(0.2, 0.19, 1.6, 8);
            const pantsMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x87ceeb, // Light blue shorts
                roughness: 0.85
            });
            
            const leftLeg = new THREE.Mesh(legGeometry, pantsMaterial);
            leftLeg.position.set(-0.25, 0.2, 0);
            group.add(leftLeg);
            
            const rightLeg = new THREE.Mesh(legGeometry, pantsMaterial);
            rightLeg.position.set(0.25, 0.2, 0);
            group.add(rightLeg);
            
            // Feet (dark blue sneakers)
            const footGeometry = new THREE.BoxGeometry(0.3, 0.2, 0.5);
            const shoeMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x00008b, // Dark blue sneakers
                roughness: 0.9
            });
            
            const leftFoot = new THREE.Mesh(footGeometry, shoeMaterial);
            leftFoot.position.set(-0.25, -0.5, 0.15);
            group.add(leftFoot);
            
            const rightFoot = new THREE.Mesh(footGeometry, shoeMaterial);
            rightFoot.position.set(0.25, -0.5, 0.15);
            group.add(rightFoot);
            
            this.name = "BIG LEZ";
            this.color = 0xff69b4; // Pink for the singlet!
        }
        
        // No name labels - bosses are visually distinct!
        // Labels only on health bar
        
        group.position.copy(position);
        group.castShadow = true;
        this.scene.add(group);
        this.mesh = group;
        
        // Add health bar
        this.createHealthBar();
    }
    
    createHealthBar() {
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 32;
        
        const texture = new THREE.CanvasTexture(canvas);
        const material = new THREE.SpriteMaterial({ map: texture });
        this.healthBarSprite = new THREE.Sprite(material);
        this.healthBarSprite.scale.set(4, 0.5, 1);
        this.healthBarSprite.position.y = 4;
        this.mesh.add(this.healthBarSprite);
        
        // Draw initial health bar with name
        this.updateHealthBar();
    }
    
    updateHealthBar() {
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 32;
        const ctx = canvas.getContext('2d');
        
        // Background
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, 256, 32);
        
        // Health
        const healthPercent = this.health / this.maxHealth;
        ctx.fillStyle = this.color === 0x6b8e23 ? '#6b8e23' : 
                        this.color === 0xffff00 ? '#ffff00' : '#ff6b6b';
        ctx.fillRect(4, 4, 248 * healthPercent, 24);
        
        // Name
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 16px Courier';
        ctx.textAlign = 'center';
        ctx.fillText(this.name, 128, 20);
        
        this.healthBarSprite.material.map = new THREE.CanvasTexture(canvas);
        this.healthBarSprite.material.needsUpdate = true;
    }
    
    attack(targetPosition) {
        const currentTime = Date.now() / 1000;
        if (currentTime - this.lastAttackTime < this.attackCooldown) return;
        
        this.lastAttackTime = currentTime;
        
        if (this.type === 'clarence') {
            this.shootMail(targetPosition);
        } else if (this.type === 'mikenolan') {
            this.shootCigarette(targetPosition);
        } else if (this.type === 'lez') {
            this.shootBong(targetPosition);
        }
    }
    
    shootMail(targetPosition) {
        // Create envelope projectile
        const group = new THREE.Group();
        
        const envelopeGeometry = new THREE.BoxGeometry(0.4, 0.3, 0.05);
        const envelopeMaterial = new THREE.MeshStandardMaterial({ color: 0xffffff });
        const envelope = new THREE.Mesh(envelopeGeometry, envelopeMaterial);
        group.add(envelope);
        
        // Red stamp
        const stampGeometry = new THREE.BoxGeometry(0.1, 0.1, 0.06);
        const stampMaterial = new THREE.MeshStandardMaterial({ color: 0xff0000 });
        const stamp = new THREE.Mesh(stampGeometry, stampMaterial);
        stamp.position.set(0.12, 0.08, 0);
        group.add(stamp);
        
        group.position.copy(this.mesh.position);
        group.position.y += 2;
        
        const direction = new THREE.Vector3();
        direction.subVectors(targetPosition, group.position);
        direction.normalize();
        group.velocity = direction.multiplyScalar(15);
        group.damage = this.damage; // Store boss damage
        
        this.projectiles.push(group);
        this.scene.add(group);
    }
    
    shootCigarette(targetPosition) {
        // Create 3-tone cigarette (filter, stem, ash)
        const group = new THREE.Group();
        
        // Filter (brown)
        const filterGeometry = new THREE.CylinderGeometry(0.05, 0.05, 0.2, 8);
        const filterMaterial = new THREE.MeshStandardMaterial({ color: 0x8b4513 });
        const filter = new THREE.Mesh(filterGeometry, filterMaterial);
        filter.rotation.z = Math.PI / 2;
        filter.position.x = -0.25;
        group.add(filter);
        
        // Stem (white)
        const stemGeometry = new THREE.CylinderGeometry(0.04, 0.04, 0.4, 8);
        const stemMaterial = new THREE.MeshStandardMaterial({ color: 0xf5f5f5 });
        const stem = new THREE.Mesh(stemGeometry, stemMaterial);
        stem.rotation.z = Math.PI / 2;
        stem.position.x = 0;
        group.add(stem);
        
        // Ash/ember (red-orange)
        const ashGeometry = new THREE.CylinderGeometry(0.04, 0.03, 0.1, 8);
        const ashMaterial = new THREE.MeshStandardMaterial({ 
            color: 0xff4500,
            emissive: 0xff4500,
            emissiveIntensity: 0.5
        });
        const ash = new THREE.Mesh(ashGeometry, ashMaterial);
        ash.rotation.z = Math.PI / 2;
        ash.position.x = 0.25;
        group.add(ash);
        
        group.position.copy(this.mesh.position);
        group.position.y += 2.5;
        
        const direction = new THREE.Vector3();
        direction.subVectors(targetPosition, group.position);
        direction.normalize();
        group.velocity = direction.multiplyScalar(18);
        group.damage = this.damage; // Store boss damage
        
        // Rotate to point forward
        group.rotation.y = Math.atan2(direction.x, direction.z);
        
        this.projectiles.push(group);
        this.scene.add(group);
    }
    
    shootBong(targetPosition) {
        // Big Lez shoots bongs! Pink and green like the show
        const group = new THREE.Group();
        
        // Base (wider bottom - pink)
        const baseGeometry = new THREE.CylinderGeometry(0.2, 0.25, 0.3, 12);
        const baseMaterial = new THREE.MeshStandardMaterial({ 
            color: 0xff69b4, // Hot pink
            transparent: true,
            opacity: 0.7
        });
        const base = new THREE.Mesh(baseGeometry, baseMaterial);
        base.position.y = -0.3;
        group.add(base);
        
        // Chamber/tube (main body - green)
        const tubeGeometry = new THREE.CylinderGeometry(0.12, 0.15, 0.6, 12);
        const tubeMaterial = new THREE.MeshStandardMaterial({ 
            color: 0x00ff00, // Bright green
            transparent: true,
            opacity: 0.6
        });
        const tube = new THREE.Mesh(tubeGeometry, tubeMaterial);
        tube.position.y = 0.1;
        group.add(tube);
        
        // Neck/mouthpiece (pink)
        const neckGeometry = new THREE.CylinderGeometry(0.08, 0.1, 0.25, 12);
        const neckMaterial = new THREE.MeshStandardMaterial({ 
            color: 0xff69b4,
            transparent: true,
            opacity: 0.7
        });
        const neck = new THREE.Mesh(neckGeometry, neckMaterial);
        neck.position.y = 0.5;
        group.add(neck);
        
        // Downstem (small tube sticking out - green)
        const downstemGeometry = new THREE.CylinderGeometry(0.04, 0.04, 0.2, 8);
        const downstemMaterial = new THREE.MeshStandardMaterial({ color: 0x00ff00 });
        const downstem = new THREE.Mesh(downstemGeometry, downstemMaterial);
        downstem.rotation.z = Math.PI / 2;
        downstem.position.set(0.15, -0.2, 0);
        group.add(downstem);
        
        group.position.copy(this.mesh.position);
        group.position.y += 2.5;
        
        const direction = new THREE.Vector3();
        direction.subVectors(targetPosition, group.position);
        direction.normalize();
        group.velocity = direction.multiplyScalar(16);
        group.damage = this.damage; // Store boss damage
        
        // Spin the bong
        group.rotationVelocity = new THREE.Vector3(
            Math.random() * 4,
            Math.random() * 4,
            Math.random() * 4
        );
        
        this.projectiles.push(group);
        this.scene.add(group);
    }
    
    takeDamage(amount) {
        this.health -= amount;
        this.updateHealthBar();
        
        if (this.health <= 0 && !this.isDead) {
            this.die();
        }
    }
    
    die() {
        this.isDead = true;
        
        // Create blood/giblet explosion!
        this.createExplosion();
        
        // Remove boss mesh immediately
        this.scene.remove(this.mesh);
    }
    
    createExplosion() {
        const bossPos = this.mesh.position.clone();
        const giblets = [];
        
        // Create 30-50 blood particles and giblets
        const numParticles = 30 + Math.floor(Math.random() * 20);
        
        for (let i = 0; i < numParticles; i++) {
            const isGiblet = Math.random() < 0.3; // 30% chance of giblet vs blood
            
            if (isGiblet) {
                // Create chunky giblet (cube or sphere) - MUCH BIGGER
                const size = 0.5 + Math.random() * 0.8; // Increased from 0.2-0.5 to 0.5-1.3
                const geometry = Math.random() < 0.5 
                    ? new THREE.BoxGeometry(size, size, size)
                    : new THREE.SphereGeometry(size, 8, 8);
                const material = new THREE.MeshStandardMaterial({ 
                    color: Math.random() < 0.5 ? 0x4a0000 : 0x6B0000, // Much darker reds
                    roughness: 0.8,
                    emissive: 0x220000, // Darker glow
                    emissiveIntensity: 0.2
                });
                const giblet = new THREE.Mesh(geometry, material);
                giblet.position.copy(bossPos);
                giblet.position.y += Math.random() * 2;
                
                // Random explosion velocity
                giblet.velocity = new THREE.Vector3(
                    (Math.random() - 0.5) * 15,
                    Math.random() * 10 + 5,
                    (Math.random() - 0.5) * 15
                );
                giblet.rotationVelocity = new THREE.Vector3(
                    Math.random() * 10,
                    Math.random() * 10,
                    Math.random() * 10
                );
                giblet.lifetime = 3 + Math.random() * 2; // 3-5 seconds
                giblet.age = 0;
                
                this.scene.add(giblet);
                giblets.push(giblet);
            } else {
                // Create blood splatter particle (BIGGER, more visible)
                const geometry = new THREE.SphereGeometry(0.3, 6, 6); // Increased from 0.1
                const material = new THREE.MeshBasicMaterial({ 
                    color: 0x550000, // Much darker blood red
                    transparent: true,
                    opacity: 1
                });
                const blood = new THREE.Mesh(geometry, material);
                blood.position.copy(bossPos);
                blood.position.y += Math.random() * 2;
                
                blood.velocity = new THREE.Vector3(
                    (Math.random() - 0.5) * 20,
                    Math.random() * 12 + 3,
                    (Math.random() - 0.5) * 20
                );
                blood.lifetime = 2 + Math.random() * 3; // 2-5 seconds
                blood.age = 0;
                
                this.scene.add(blood);
                giblets.push(blood);
            }
        }
        
        // Animate giblets
        const updateGiblets = () => {
            const deltaTime = 0.016; // ~60fps
            
            for (let i = giblets.length - 1; i >= 0; i--) {
                const giblet = giblets[i];
                giblet.age += deltaTime;
                
                // Apply gravity and velocity
                giblet.velocity.y -= 9.8 * deltaTime; // Gravity
                giblet.position.add(giblet.velocity.clone().multiplyScalar(deltaTime));
                
                // Rotate if it has rotation velocity
                if (giblet.rotationVelocity) {
                    giblet.rotation.x += giblet.rotationVelocity.x * deltaTime;
                    giblet.rotation.y += giblet.rotationVelocity.y * deltaTime;
                    giblet.rotation.z += giblet.rotationVelocity.z * deltaTime;
                }
                
                // Fade out near end of lifetime
                const fadeStart = giblet.lifetime * 0.7;
                if (giblet.age > fadeStart) {
                    const fadeProgress = (giblet.age - fadeStart) / (giblet.lifetime - fadeStart);
                    giblet.material.opacity = 1 - fadeProgress;
                    giblet.material.transparent = true;
                }
                
                // Remove when lifetime exceeded or hit ground
                if (giblet.age > giblet.lifetime || giblet.position.y < 0) {
                    this.scene.remove(giblet);
                    giblets.splice(i, 1);
                }
            }
            
            // Continue animation if giblets remain
            if (giblets.length > 0) {
                requestAnimationFrame(updateGiblets);
            }
        };
        
        updateGiblets();
    }
    
    getTerrainHeight(position) {
        if (!this.world) return 0;
        
        // Raycast down from high above to find terrain
        const raycaster = new THREE.Raycaster();
        const rayOrigin = new THREE.Vector3(position.x, 100, position.z);
        const rayDirection = new THREE.Vector3(0, -1, 0);
        
        raycaster.set(rayOrigin, rayDirection);
        
        // Check all terrain meshes
        const terrainMeshes = [];
        
        if (this.world.island) {
            terrainMeshes.push(this.world.island);
        }
        
        if (this.world.volcano && this.world.volcano.terrain) {
            terrainMeshes.push(this.world.volcano.terrain);
        }
        
        if (this.world.beach) {
            terrainMeshes.push(this.world.beach);
        }
        
        const intersects = raycaster.intersectObjects(terrainMeshes, false);
        
        if (intersects.length > 0) {
            return Math.max(...intersects.map(hit => hit.point.y));
        }
        
        return 0;
    }
    
    update(deltaTime, playerPosition) {
        if (this.isDead) return;
        
        // Speak occasionally during combat
        if (Math.random() < 0.005) { // 0.5% chance per frame
            this.speak();
        }
        
        // Move slowly toward player
        const direction = new THREE.Vector3();
        direction.subVectors(playerPosition, this.mesh.position);
        const distance = direction.length();
        direction.normalize();
        
        // Keep distance but move closer if too far
        if (distance > 15) {
            this.mesh.position.add(direction.multiplyScalar(this.speed * deltaTime));
            
            // Follow terrain height (walk over volcano!)
            const terrainHeight = this.getTerrainHeight(this.mesh.position);
            const bossHeight = 1; // Height above ground
            if (this.mesh.position.y < terrainHeight + bossHeight) {
                this.mesh.position.y = terrainHeight + bossHeight;
            }
        }
        
        // Lez volcano mechanics
        if (this.canFly && this.type === 'lez' && !this.isFlying) {
            const currentTime = Date.now() / 1000;
            if (currentTime - this.lastVolcanoTime > this.volcanoCooldown && this.health < this.maxHealth * 0.7) {
                this.flyToVolcano();
            }
        }
        
        // Update flying behavior
        if (this.isFlying) {
            this.updateFlying(deltaTime);
        }
        
        // Look at player
        this.mesh.lookAt(playerPosition);
        
        // Attack player
        this.attack(playerPosition);
        
        // Update projectiles
        this.projectiles.forEach((projectile, index) => {
            projectile.position.add(projectile.velocity.clone().multiplyScalar(deltaTime));
            
            // Spin beer cans
            if (projectile.rotationVelocity) {
                projectile.rotation.x += projectile.rotationVelocity.x * deltaTime;
                projectile.rotation.y += projectile.rotationVelocity.y * deltaTime;
                projectile.rotation.z += projectile.rotationVelocity.z * deltaTime;
            }
            
            // Remove if too far
            if (projectile.position.distanceTo(this.mesh.position) > 100) {
                this.scene.remove(projectile);
                this.projectiles.splice(index, 1);
            }
        });
    }
    
    flyToVolcano() {
        this.isFlying = true;
        this.flyPhase = 'ascending'; // ascending → traveling → descending → healing → emerging
        this.flyStartY = this.mesh.position.y;
        this.flyTargetHeight = 35; // Above volcano
        this.lastVolcanoTime = Date.now() / 1000;
    }
    
    updateFlying(deltaTime) {
        const volcanoPos = new THREE.Vector3(0, 0, 0);
        
        if (this.flyPhase === 'ascending') {
            // Fly up
            this.mesh.position.y += 10 * deltaTime;
            if (this.mesh.position.y >= this.flyTargetHeight) {
                this.flyPhase = 'traveling';
                console.log('✈️ Lez traveling to volcano...');
            }
        }
        else if (this.flyPhase === 'traveling') {
            // Fly to volcano center
            const direction = new THREE.Vector3();
            direction.subVectors(volcanoPos, this.mesh.position);
            direction.y = 0; // Don't change height yet
            const distance = direction.length();
            direction.normalize();
            
            this.mesh.position.add(direction.multiplyScalar(15 * deltaTime));
            
            if (distance < 5) {
                this.flyPhase = 'descending';
                console.log('🔻 Lez descending into volcano...');
            }
        }
        else if (this.flyPhase === 'descending') {
            // Descend into crater
            this.mesh.position.y -= 8 * deltaTime;
            if (this.mesh.position.y <= 25) { // Inside crater
                this.flyPhase = 'healing';
                this.healStartTime = Date.now() / 1000;
                console.log('💚 Lez healing inside volcano!');
            }
        }
        else if (this.flyPhase === 'healing') {
            // Heal over 3 seconds
            const healDuration = 3;
            const currentTime = Date.now() / 1000;
            const elapsed = currentTime - this.healStartTime;
            
            if (elapsed < healDuration) {
                // Gradual healing
                const healAmount = (this.maxHealth * 0.5 * deltaTime) / healDuration;
                this.health = Math.min(this.maxHealth, this.health + healAmount);
                this.updateHealthBar();
                
                // Glow effect - store original colors first!
                if (!this.originalColors) {
                    this.originalColors = new Map();
                    this.mesh.traverse(child => {
                        if (child.material && child.material.color) {
                            this.originalColors.set(child, {
                                color: child.material.color.clone(),
                                emissive: child.material.emissive ? child.material.emissive.clone() : null,
                                emissiveIntensity: child.material.emissiveIntensity || 0
                            });
                        }
                    });
                }
                
                // Apply glow (ONLY to StandardMaterial, keep color intact)
                this.mesh.traverse(child => {
                    if (child.material && child.material.emissive && child.material.type === 'MeshStandardMaterial') {
                        child.material.emissive = new THREE.Color(0xff2200);
                        child.material.emissiveIntensity = 0.5;
                    }
                });
            } else {
                this.flyPhase = 'emerging';
                console.log('⬆️ Lez emerging - faster and stronger!');
                // Speed boost!
                this.speed = 4; // Was 2, now 4!
            }
        }
        else if (this.flyPhase === 'emerging') {
            // Rise back up
            this.mesh.position.y += 12 * deltaTime;
            if (this.mesh.position.y >= this.flyTargetHeight) {
                this.flyPhase = 'returning';
                console.log('🔙 Lez returning to battle!');
            }
        }
        else if (this.flyPhase === 'returning') {
            // Fly back to battle position
            this.mesh.position.y -= 10 * deltaTime;
            if (this.mesh.position.y <= this.flyStartY) {
                this.mesh.position.y = this.flyStartY;
                this.isFlying = false;
                this.flyPhase = null;
                
                // Restore original colors and remove glow
                if (this.originalColors) {
                    this.mesh.traverse(child => {
                        if (child.material && this.originalColors.has(child)) {
                            const orig = this.originalColors.get(child);
                            child.material.color.copy(orig.color);
                            if (orig.emissive && child.material.emissive) {
                                child.material.emissive.copy(orig.emissive);
                            }
                            if (child.material.emissiveIntensity !== undefined) {
                                child.material.emissiveIntensity = orig.emissiveIntensity;
                            }
                        }
                    });
                    this.originalColors = null; // Clear for next time
                }
                
                console.log('⚡ Lez back in the fight - WATCH OUT!');
            }
        }
    }
    
    destroy() {
        this.scene.remove(this.mesh);
        this.projectiles.forEach(p => this.scene.remove(p));
    }
}
